/** Support timestamps are UTC on the wire and local calendar dates in the UI. */
export const parseSupportTimestamp = (value: string | null | undefined): Date | null => {
  if (!value?.trim()) return null;
  const text = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(text)) return null;
  if (!parseSupportCalendarDate(text.slice(0, 10))) return null;
  const explicitZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text);
  const result = new Date(explicitZone ? text : `${text}Z`);
  return Number.isFinite(result.getTime()) ? result : null;
};

export const formatSupportTime = (value: string | null): string => {
  const date = parseSupportTimestamp(value);
  return date?.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) ?? '—';
};

export const formatSupportDateTime = (value: string | null): string => {
  const date = parseSupportTimestamp(value);
  return date ? date.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '—';
};

export const formatSupportTimeAgo = (value: string | null, now = Date.now()): string => {
  const date = parseSupportTimestamp(value);
  if (!date) return '—';
  const elapsed = now - date.getTime();
  // Tolerate small clock skew; do not describe genuinely future dates as past.
  if (elapsed < -60000) return formatSupportDateTime(value);
  const minutes = Math.floor(Math.max(0, elapsed) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days < 7 ? `${days}d ago` : date.toLocaleDateString();
};

const localDayNumber = (date: Date): number =>
  Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000;

export const sameSupportDay = (first: string | null, second: string | null): boolean => {
  const a = parseSupportTimestamp(first);
  const b = parseSupportTimestamp(second);
  return !!a && !!b && localDayNumber(a) === localDayNumber(b);
};

export const supportDateLabel = (value: string | null, now = new Date()): string => {
  const date = parseSupportTimestamp(value);
  if (!date) return '—';
  const days = localDayNumber(now) - localDayNumber(date);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days > 1 && days < 7) return date.toLocaleDateString(undefined, { weekday: 'long' });
  return date.toLocaleDateString();
};

export const localSupportDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const parseSupportCalendarDate = (value: string): Date | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (year < 1) return null;
  const date = new Date(0);
  date.setFullYear(year, month - 1, day);
  date.setHours(0, 0, 0, 0);
  return localSupportDate(date) === value ? date : null;
};

export const supportRangeError = (start: string, end: string): string | null => {
  if ((start && !parseSupportCalendarDate(start)) || (end && !parseSupportCalendarDate(end))) {
    return 'Enter valid dates.';
  }
  return start && end && start > end ? 'From date must be on or before To date.' : null;
};

export const formatSupportDateRange = (start: string, end: string): string => {
  const format = (value: string) => parseSupportCalendarDate(value)?.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  }) ?? '—';
  if (!start && !end) return 'All dates';
  if (!start) return `Through ${format(end)}`;
  if (!end) return `From ${format(start)}`;
  return `${format(start)} – ${format(end)}`;
};
